import { useCallback } from 'react';

import { useKibanaLogger } from '../../../shared/hooks/kibana-logger/kibana-logger';
import { dataBuilderStore } from '../data-builder.store';
import type { BuildPayloadInput } from './build-verify-payload';
import {
  type BuildVerifyPayloadResult,
  buildVerifyPayload,
  buildVerifyPayloadWithStateMerge,
  ROW_LIMIT_PROBE_STATE_MERGE,
} from './build-verify-payload';
import type { DataBuilderSchema } from './data-builder-api.types';

export function useBuildDataBuilderPayload(
  input: Pick<BuildPayloadInput, 'client' | 'name'> & {
    datasets?: BuildPayloadInput['datasets'];
    rowLimitValidationProbe?: boolean;
  }
) {
  const { kibanaLogger } = useKibanaLogger();

  const build = useCallback(
    (
      schema: DataBuilderSchema,
      instanceIds?: ReadonlySet<string>
    ): BuildVerifyPayloadResult | null => {
      const state = dataBuilderStore.getState();

      try {
        const payloadInput = {
          schema,
          state: {
            datasetInstances: state.datasetInstances,
            configByInstance: state.configByInstance,
            lagData: state.lagData,
            deliveryMethod: state.deliveryMethod,
            deliveryFormat: state.deliveryFormat,
            snowflakeConfig: state.snowflakeConfig,
            s3Config: state.s3Config,
            modelVersions: state.modelVersions,
          },
          client: input.client,
          name: input.name,
          datasets: input.datasets,
          instanceIds,
        };

        if (input.rowLimitValidationProbe === true) {
          return buildVerifyPayloadWithStateMerge(
            payloadInput,
            ROW_LIMIT_PROBE_STATE_MERGE
          );
        }

        return buildVerifyPayload(payloadInput);
      } catch (_error: unknown) {
        kibanaLogger?.error(
          `Data Builder: payload build failed. ${_error instanceof Error ? _error.message : String(_error)}`
        );
        return null;
      }
    },
    [
      input.client,
      input.datasets,
      input.name,
      input.rowLimitValidationProbe,
      kibanaLogger,
    ]
  );

  return { build };
}
